/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  DeploymentAction,
  DeploymentAction$inboundSchema,
} from "./deploymentaction.js";
import {
  DeploymentDocker,
  DeploymentDocker$inboundSchema,
} from "./deploymentdocker.js";
import {
  DeploymentError,
  DeploymentError$inboundSchema,
} from "./deploymenterror.js";
import { DeploymentGit, DeploymentGit$inboundSchema } from "./deploymentgit.js";
import {
  DeploymentRuntime,
  DeploymentRuntime$inboundSchema,
} from "./deploymentruntime.js";
import {
  DeploymentStatus,
  DeploymentStatus$inboundSchema,
} from "./deploymentstatus.js";

export type Deployment = {
  /**
   * The unique identifier of the deployment, generated by Teral.
   *
   * @remarks
   */
  id: string;
  /**
   * Current lifecycle status of the deployment. Poll until it reaches a
   *
   * @remarks
   * terminal state: ready (serving), failed, skipped, superseded, stopped,
   * or cancelled.
   */
  status: DeploymentStatus;
  /**
   * True when this is the production deployment currently serving traffic, i.e.
   *
   * @remarks
   * on api.acme.com. Only production deployments can be current, and at most one
   * deployment is current. Rollbacks and promotions change which deployment is
   * current and serves requests to api.acme.com.
   */
  isCurrent: boolean;
  /**
   * Slug of the environment this deployment belongs to.
   */
  environment: string;
  /**
   * Slug of the app this deployment belongs to.
   */
  app: string;
  /**
   * Slug of the project this deployment belongs to.
   */
  project: string;
  git?: DeploymentGit | undefined;
  docker?: DeploymentDocker | undefined;
  /**
   * Lifecycle operations you are allowed to call on this deployment right now.
   *
   * @remarks
   * Empty when none apply (e.g. while building or in a terminal state).
   */
  availableActions: Array<DeploymentAction>;
  /**
   * Regions this deployment is configured to run in. Empty while the deployment
   *
   * @remarks
   * has no scheduled regions yet.
   */
  regions: Array<string>;
  error?: DeploymentError | undefined;
  /**
   * Public hostnames this deployment is reachable at.
   *
   * @remarks
   */
  domains?: Array<string> | undefined;
  runtime: DeploymentRuntime;
  /**
   * Unix timestamp in milliseconds when the deployment was created.
   *
   * @remarks
   */
  createdAt: number;
  /**
   * Unix timestamp in milliseconds when the deployment was last updated.
   *
   * @remarks
   * Omitted if the deployment has never been updated.
   */
  updatedAt?: number | undefined;
};

/** @internal */
export const Deployment$inboundSchema: z.ZodType<
  Deployment,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  status: DeploymentStatus$inboundSchema,
  isCurrent: z.boolean(),
  environment: z.string(),
  app: z.string(),
  project: z.string(),
  git: DeploymentGit$inboundSchema.optional(),
  docker: DeploymentDocker$inboundSchema.optional(),
  availableActions: z.array(DeploymentAction$inboundSchema),
  regions: z.array(z.string()),
  error: DeploymentError$inboundSchema.optional(),
  domains: z.array(z.string()).optional(),
  runtime: DeploymentRuntime$inboundSchema,
  createdAt: z.number().int(),
  updatedAt: z.number().int().optional(),
});

export function deploymentFromJSON(
  jsonString: string,
): SafeParseResult<Deployment, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Deployment$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Deployment' from JSON`,
  );
}
